import React, { useEffect, useRef, useState } from "react";
import { ReportGraphWrapper } from "../style";
import { Bar } from "react-chartjs-2";
import ChartDataLabels from "chartjs-plugin-datalabels";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  PointElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";
import {
  SettledDailyReportResponse,
  SettlementExpenseDto,
} from "../../../../../api/settlement";
import { formatDate } from "../../../../../utils/date";
ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  PointElement,
  Title,
  Tooltip,
  Legend,
  ChartDataLabels
);

interface ReportProps {
  reportData: SettledDailyReportResponse[];
  firstDate: string;
  lastDate: string;
  setFirstDate: (value: string) => void;
  setLastDate: (value: string) => void;
  setIsReport: (value: boolean) => void;
  setDailyDate: (value: string) => void;
  dateFilterType: string;
}

const ReportGraph = ({
  reportData,
  firstDate,
  lastDate,
  setFirstDate,
  setLastDate,
  setIsReport,
  setDailyDate,
  dateFilterType,
}: ReportProps) => {
  const [labels, setLabels] = useState<string[]>([]); // 각 데이터 날짜 (mm.dd)
  const [payCountList, setPayCountList] = useState<number[]>([]); // 결제 건수
  const [barData, setBarData] = useState<string[]>([]); // 매출 막대바
  const [incomeList, setIncomeList] = useState<number[]>([]);
  const [expenditureList, setExpenditureList] = useState<number[]>([]);
  const [dailyList, setDailyList] = useState<string[]>([]); // 각 데이터 날짜 (yyyy-dd-mm)
  const [animationDuration, setAnimationDuration] = useState<number>(500);
  const prevReportData = useRef(reportData);

  useEffect(() => {
    const today = new Date();
    let startDate = new Date();
    let endDate = new Date();
    if (dateFilterType === "date") {
      startDate = new Date(firstDate);
      endDate = new Date(lastDate);
      setLastDate(lastDate);
    } else if (dateFilterType === "this_week") {
      startDate = new Date(today);
      startDate.setDate(today.getDate() - (today.getDay() || 7) + 1); // 이번주 월요일

      endDate = new Date(today);
      endDate.setDate(endDate.getDate());
      setLastDate(today.toISOString().split("T")[0]);
    } else if (dateFilterType === "this_month") {
      startDate = new Date(today.getFullYear(), today.getMonth(), 2);

      endDate = new Date(today);
      endDate.setDate(endDate.getDate() + 1);
      setLastDate(today.toISOString().split("T")[0]);
    } else if (dateFilterType === "last_month") {
      startDate = new Date(today.getFullYear(), today.getMonth() - 1, 2);

      endDate = new Date(today.getFullYear(), today.getMonth(), 1);
      setLastDate(
        new Date(today.getFullYear(), today.getMonth(), 1)
          .toISOString()
          .split("T")[0]
      );
    }
    setFirstDate(startDate.toISOString().split("T")[0]);
    setLabelsAndDate(startDate, endDate);
  }, [reportData]);

  useEffect(() => {
    if (!reportData) return;
    let daily: string[] = [];
    let tempBarData: number[] = [];
    let tempLineData: string[] = [];
    let incomeList: number[] = [];
    let expenditure: number[] = [];
    let expenseList: SettlementExpenseDto[] = [];

    reportData.forEach((item) => {
      daily.push(formatDate(item.settledAt));
    });

    dailyList.forEach((date) => {
      const matchingIndex = daily.indexOf(date);
      if (matchingIndex !== -1) {
        const item = reportData[matchingIndex];
        tempBarData.push(item.entryCount);
        tempLineData.push(item.finalNetProfit?.toString());
        incomeList.push(item.totalRevenue);
        expenditure.push(item.totalExpense);
        expenseList.push(...item.expense);
      } else {
        tempBarData.push(0);
        tempLineData.push("0");
        incomeList.push(0);
        expenditure.push(0);
      }
    });

    setPayCountList(tempBarData);
    setBarData(tempLineData);
    setIncomeList(incomeList);
    setExpenditureList(expenditure);
  }, [dailyList]);

  const setLabelsAndDate = (start: Date, end: Date) => {
    const dates: string[] = [];
    const labels: string[] = [];
    const current = new Date(start);

    while (current <= end) {
      const [yyyy, mm, dd] = current.toISOString().split("T")[0].split("-");
      dates.push(`${yyyy}-${mm}-${dd}`);
      labels.push(`${mm}.${dd}`);
      current.setDate(current.getDate() + 1);
    }
    setDailyList(dates);
    setLabels(labels);
  };

  useEffect(() => {
    if (reportData !== prevReportData.current) {
      setAnimationDuration(500);
      prevReportData.current = reportData;
      setTimeout(() => {
        setAnimationDuration(0);
      }, 500);
    } else {
      setAnimationDuration(0);
    }
  }, [reportData]);

  const getReportDate = (dataDay: any) => {
    const today = new Date(dataDay);
    const month = String(today.getMonth() + 1).padStart(2, "0");
    const day = String(today.getDate()).padStart(2, "0");

    return `${month}/${day}`;
  };

  const data = {
    labels: labels,
    datasets: [
      {
        backgroundColor: "#E9ECF1",
        borderColor: "#E9ECF1",
        data: barData,
        borderWidth: 2,
        yAxisID: "y1",
        borderRadius: 11.24,
        datalabels: {
          display: true,
          anchor: "end" as const,
          align: "top" as const,
          padding: 10,
          formatter: (value: any, context: any) => {
            const index = context.dataIndex;
            return `${payCountList[index]}건`;
          },
          color: "#121212",
          font: {
            size: labels.length > 15 ? 12 : 18,
            weight: 600,
            family: "Pretendard",
          },
        },
      },
    ],
  };

  const options = {
    responsive: true,
    maxBarThickness: 25,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: false,
      },
      tooltip: {
        enabled: false,
        external: function (tooltip: any) {
          const tooltipEl = document.getElementById("chartjs-tooltip");
          const graph = document.getElementById("graph");

          if (!tooltipEl) return;
          if (!tooltip.tooltip.dataPoints) return;

          tooltipEl.style.display = "block";
          let x = tooltip.tooltip.dataPoints[0].element.x;
          let y = tooltip.tooltip.dataPoints[0].element.y + 19;
          if (x > 1230) x = 1230;
          if (y > 225) y = 225;

          tooltipEl.style.left = x + "px";
          tooltipEl.style.top = y + "px";

          const chart = tooltip.chart;
          const tooltipDataPoint = tooltip.tooltip.dataPoints[0];

          const labelList = chart.config._config.data.labels;
          const dataDate = tooltipDataPoint.label;

          const index = labelList.findIndex(
            (label: string) => label === dataDate
          );

          const dataDay = tooltip.tooltip.dataPoints[0].label;
          const innerHTML = `
                <div class="header">
                  <div>${getReportDate(dataDay)} 정산 리포트</div>
                  <div class="move-page">
                    <img src="/image-web/solution/right.svg" alt="right" />
                  </div>
                </div>
                <div class="info-box">
                  <div class="info">
                    <div class="name">매출액</div>
                    <div class="value">${incomeList[
                      index
                    ]?.toLocaleString()}원</div>
                  </div>
                  <div class="info">
                    <div class="name">지출액</div>
                    <div class="value">${expenditureList[
                      index
                    ]?.toLocaleString()}원</div>
                  </div>
                  <div class="info total">
                    <div class="name">정산액</div>
                    <div class="value">${parseInt(
                      barData[index]
                    )?.toLocaleString()}원</div>
                  </div>
                </div>
              `;
          tooltipEl.innerHTML = innerHTML;

          tooltipEl.addEventListener("mouseleave", () => {
            tooltipEl.style.display = "none";
          });

          if (graph) {
            graph.addEventListener("mouseleave", () => {
              tooltipEl.style.display = "none";
            });
          }

          const move = document.getElementsByClassName("move-page");
          for (let i = 0; i < move.length; i++) {
            move[i].addEventListener("click", () => {
              setIsReport(false);
              setDailyDate(dailyList[index]);
            });
          }
        },
      },
    },
    elements: {
      bar: {
        hoverBackgroundColor: "#E9ECF1",
        hoverBorderColor: "#E9ECF1",
      },
      point: {
        hoverBackgroundColor: "#7F25FB",
        hoverBorderColor: "#7F25FB",
      },
    },
    animation: {
      duration: animationDuration,
    },
    scales: {
      y1: {
        afterDataLimits: (scale: any) => {
          scale.max = scale.max * 1.2 === 0 ? 1 : scale.max * 1.2;
        },
        gridWidth: 3,
        ticks: {
          stepSize: 1000000,
          callback: (value: any) => value?.toLocaleString() + "원",
          font: {
            size: 15,
            color: "#797979",
            weight: 600,
            family: "Pretendard",
          },
          padding: 20,
        },
        border: {
          width: 0,
        },
        grid: {
          color: "#D9D9D9",
          lineWidth: 2.25,
        },
      },
      x: {
        grid: {
          display: false,
        },
        ticks: {
          font: {
            size: labels.length > 15 ? 11 : 15,
            color: "#121212",
            weight: 400,
            family: "Pretendard",
          },
          callback: function (value: any, index: any, ticks: any) {
            if (labels.length > 25) {
              return index % 5 === 0 ? labels[index] : "";
            }
            return labels[index];
          },
        },
        border: {
          width: 0,
        },
      },
    },
  };

  return (
    <ReportGraphWrapper>
      <Bar data={data} options={options} id="graph" />
      <div id="chartjs-tooltip" />
    </ReportGraphWrapper>
  );
};

export default ReportGraph;
